"use client";

import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import { usePathname, useRouter } from "next/navigation";
import { Suspense, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { EASE } from "@/lib/motion";
import { useReferralCapture } from "@/lib/api/referral-intake";
import { SPORTSBOOK_ROOT, usesLabelledRail, usesBarRail, usesMenuDrawer, usesTileNav, usesTopbarNav } from "@/lib/site";
import { cn } from "@/lib/cn";
import type { NavItem, SiteContent } from "@/lib/content/types";
import type { SmarticoConfig } from "@/lib/smartico/config";
import { SmarticoProvider } from "@/lib/smartico/SmarticoProvider";
import { ChatPanel } from "./ChatPanel";
import { GuestChatPanel } from "./GuestChatPanel";
import { ContentContext } from "./ContentContext";
import { useIdentity } from "@/lib/api/session";
import { SiteNameContext } from "./SiteNameContext";
import { MobileTabBar } from "./MobileTabBar";
import { PlatformEventsProvider } from "./PlatformEvents";
import { WalletDeepLink } from "./WalletDeepLink";
import type { DepositPreset } from "@/lib/wallet/deep-link";
import { PlayerProvider } from "./PlayerContext";
import { ShellContext } from "./ShellContext";
import { CollapsedSideNav, LabelledRail, SideNav } from "./SideNav";
import { TileNav } from "./TileNav";
import { TopBar } from "./TopBar";
import { I18nProvider } from "@/lib/i18n/I18nProvider";
import { useModalChrome } from "@/components/ui/Modal";
import { ToastProvider } from "@/components/ui/Toast";
import { WalletModal, type WalletTab } from "@/components/wallet/WalletModal";
import { SignInModal } from "@/components/auth/SignInModal";
import { KycModal } from "@/components/kyc/KycModal";
import { TelegramMiniApp } from "./TelegramMiniApp";

export function AppShell({
  content,
  siteName,
  casinoNav,
  smartico,
  geo,
  previewing,
  children,
}: {
  content: SiteContent;
  siteName: string;
  casinoNav: NavItem[];
  smartico: SmarticoConfig | null;
  geo: { isGeoblocked: boolean } | null;
  previewing: boolean;
  children: ReactNode;
}) {
  const pathname = usePathname();
  const tileLayout = usesTileNav(content.flags.navLayout);
  const labelledRail = usesLabelledRail(content.flags.navLayout);
  const topbar = usesTopbarNav(content.flags.navLayout);
  /* Combined: the phone's menu drawer is the desktop's side menu too. */
  const menuDrawer = usesMenuDrawer(content.flags.navLayout);
  const chatLeft = content.flags.chatSide === "Left";
  /* Over a site backdrop or the night sky the shell is clear, so the image or
     the stars show through. */
  const canvas = content.theme.backdropUrl?.trim() || content.theme.motion === "Night sky" ? "" : "bg-canvas";
  /* Feature flags -> Page width -> Centered: the whole site in a centred
     column, whatever surrounds it showing either side. */
  const boxed = content.flags.pageWidth === "Centered" ? "mx-auto w-full max-w-[1440px]" : "";
  const iconRail = content.flags.collapsedRail;

  /* A referral link can land on any page, and the shell is the one component
     on all of them — so the code is caught here, validated, and held for
     whichever sign-in method the player eventually picks. */
  useReferralCapture();
  const [navOpen, setNavOpen] = useState(false);
  /* Combined rail: the rail under the bar, widened to its labels by the bar's
     menu button on desktop (the phone keeps the drawer, from the tab bar). */
  const barRailLayout = usesBarRail(content.flags.navLayout);
  const [barRailOpen, setBarRailOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(true);
  const [chatOpen, setChatOpen] = useState(true);
  const [signIn, setSignIn] = useState<{ open: boolean; email: string }>({ open: false, email: "" });
  const [wallet, setWallet] = useState<{ open: boolean; tab: WalletTab; preset?: DepositPreset }>({
    open: false,
    tab: "Deposit",
  });

  const openWallet = useCallback(
    (tab: WalletTab = "Deposit", preset?: DepositPreset) => setWallet({ open: true, tab, preset }),
    [],
  );
  const closeWallet = useCallback(() => setWallet((current) => ({ ...current, open: false })), []);
  const openChat = useCallback(() => setChatOpen(true), []);
  const openSignIn = useCallback(
    (email?: string) => setSignIn({ open: true, email: typeof email === "string" ? email : "" }),
    [],
  );

  const drawer = useModalChrome(navOpen, () => setNavOpen(false));

  /* The rail is `xl:` and up, and a hidden panel is not a free one: mounted, it
     still fetches identity and rooms, mints an Ably token and opens the
     platform socket. Every phone visitor was holding one of Ably's 200
     concurrent connections for a UI they cannot see or reach. Below the
     breakpoint it is not rendered at all.

     Matched in an effect rather than during render, so the server and the first
     client pass agree. */
  const [wideEnoughForChat, setWideEnoughForChat] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(min-width: 1280px)");
    const sync = () => setWideEnoughForChat(query.matches);

    sync();
    query.addEventListener("change", sync);

    return () => query.removeEventListener("change", sync);
  }, []);

  /* Chat is for signed-in players only: a visitor with no session has no name
     to post under and nothing to gain from a room they cannot join, so the
     panel does not exist for them, toggle and all (docs/chat-plan.md §6). */
  const { isSignedIn } = useIdentity();

  /* Custom HTML cannot reach the shell, so any link to "#sign-in" — a
     template's last call to sign up — opens the sign-in; signed in, it opens
     the casino lobby instead. */
  const router = useRouter();
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const link = event.target instanceof Element ? event.target.closest('a[href="#sign-in"]') : null;
      if (!link) return;

      event.preventDefault();
      if (isSignedIn) router.push("/casino");
      else openSignIn();
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [isSignedIn, openSignIn, router]);
  const chatVisible = chatOpen && content.flags.chatEnabled && wideEnoughForChat && isSignedIn;
  /* Unless the operator lets visitors look in (Feature flags → Visitors see
     the chat): then a signed-out visitor gets the room read-only, polled,
     with no realtime connection behind it. */
  const guestChat = chatOpen && content.flags.chatEnabled && content.flags.chatPreview && wideEnoughForChat && !isSignedIn;
  /* The left-hand chat is the page's second column, so it is wider. */
  const chatWidth = chatLeft ? 320 : 256;
  const [kycOpen, setKycOpen] = useState(false);
  const openKyc = useCallback(() => setKycOpen(true), []);
  const closeKyc = useCallback(() => setKycOpen(false), []);

  const shell = useMemo(
    () => ({ chatVisible, openWallet, closeWallet, openChat, openSignIn, openKyc }),
    [chatVisible, openWallet, closeWallet, openChat, openSignIn, openKyc],
  );

  /* What counts as "a different page" for the transition. Normally the path,
     but the sportsbook renderer rewrites the URL as the visitor moves through
     it — animating that would remount the book and throw away their betslip,
     so the whole subtree is one page. */
  const transitionKey = pathname.startsWith(SPORTSBOOK_ROOT) ? SPORTSBOOK_ROOT : pathname;

  if (pathname.startsWith("/admin")) {
    return (
      <MotionConfig reducedMotion="user">
        <ContentContext.Provider value={content}>
          <div className="min-h-dvh bg-canvas">{children}</div>
        </ContentContext.Provider>
      </MotionConfig>
    );
  }

  const skipLink = (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:left-[16px] focus:top-[16px] focus:z-50 focus:grid focus:h-[38px] focus:place-items-center focus:rounded-lg focus:bg-brand focus:px-[12px] focus:text-[12px] focus:font-bold focus:leading-[1.6] focus:text-fg-on-primary"
    >
      Skip to content
    </a>
  );

  /* Desktop rail — the Tile template folds this into the top of the centre
     column instead, via TileNav; Rail draws its own labelled rail; Topbar
     has none. Collapsing leaves the icon rail when the flag says so,
     nothing otherwise. */
  const rail = labelledRail ? (
    <div className="sticky top-0 hidden h-dvh lg:block">
      <LabelledRail casinoNav={casinoNav} />
    </div>
  ) : !tileLayout && !topbar ? (
    <motion.div
      initial={false}
      animate={{ width: railOpen ? 256 : iconRail ? 64 : 0 }}
      transition={{ duration: 0.32, ease: EASE }}
      className="sticky top-0 hidden h-dvh overflow-hidden lg:block"
    >
      {railOpen ? (
        <SideNav onClose={() => setRailOpen(false)} casinoNav={casinoNav} />
      ) : iconRail ? (
        <CollapsedSideNav onOpen={() => setRailOpen(true)} casinoNav={casinoNav} />
      ) : null}
    </motion.div>
  ) : null;

  /* Combined rail: the menu's icons stay on screen under the bar on desktop,
     and the bar's menu button slides the whole menu in over them (Capella's,
     after Betmode). */
  /* It keeps a 64px seat in the row and widens over the page, not pushing
     it, when the bar's menu button opens it. */
  const barRail = barRailLayout ? (
    <div className="sticky top-20 z-30 hidden h-[calc(100dvh-80px)] w-16 shrink-0 lg:block">
      <motion.div
        initial={false}
        animate={{ width: barRailOpen ? 256 : 64 }}
        transition={{ duration: 0.28, ease: EASE }}
        className={cn(
          "absolute inset-y-0 left-0 overflow-hidden",
          barRailOpen && "shadow-[24px_0_64px_rgba(0,0,0,0.42)]",
        )}
      >
        <CollapsedSideNav
          onOpen={() => setBarRailOpen(true)}
          casinoNav={casinoNav}
          underBar
          expanded={barRailOpen}
        />
      </motion.div>
    </div>
  ) : null;

  const mobileDrawer = (
    <AnimatePresence>
      {navOpen && (
        <div
          className={cn(
            "fixed inset-0 bottom-[calc(62px+var(--safe-bottom))] z-50",
            menuDrawer ? "lg:bottom-0" : "lg:hidden",
          )}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setNavOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
          />
          <motion.div
            ref={drawer}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ duration: 0.34, ease: EASE }}
            className={cn("relative h-full w-full max-w-[420px] pl-[env(safe-area-inset-left)]", menuDrawer && "lg:max-w-[300px]")}
          >
            <SideNav onClose={() => setNavOpen(false)} onNavigate={() => setNavOpen(false)} casinoNav={casinoNav} />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  const topBar = (
    <TopBar
      onWallet={() => openWallet("Deposit")}
      onChat={() => setChatOpen((c) => !c)}
      onRail={() => setRailOpen((r) => !r)}
      onMenu={
        barRailLayout ? () => setBarRailOpen((open) => !open) : menuDrawer ? () => setNavOpen((n) => !n) : undefined
      }
      menuOpen={barRailLayout ? barRailOpen : navOpen}
      railOpen={tileLayout || labelledRail || topbar || railOpen || iconRail}
      chatOpen={chatVisible}
      siteName={siteName}
      casinoNav={casinoNav}
    />
  );

  const main = (
    <main
      id="main-content"
      tabIndex={-1}
      className="min-w-0 flex-1 pb-[calc(62px+var(--safe-bottom))] lg:pb-0"
    >
      {(geo === null || geo.isGeoblocked || previewing) && (
        <div className="mx-auto w-full max-w-[1144px] px-[16px] pt-[16px] lg:px-[24px]">
          <div className="rounded-xl border border-border bg-elevation-2 px-[16px] py-[12px] text-[12px] leading-[1.6] text-fg-subtle">
            {previewing
              ? geo?.isGeoblocked
                ? "You're previewing draft content from an admin session. Your own location is not licensed for play, so this preview is geoblocked too — a real visitor elsewhere may see a different result."
                : "You're previewing draft content from an admin session. Nothing here is saved or visible to a visitor until you Save a section."
              : geo?.isGeoblocked
                ? "This site is not licensed for play from your location. You can browse the catalogue, but games and sign-in are restricted here."
                : "We could not confirm whether this site is licensed for play from your location. Games and sign-in may be restricted here."}
          </div>
        </div>
      )}
      {/* No AnimatePresence around `children` — see CLAUDE.md. An exit
          animation promises a descendant will report back, and a page
          subtree cannot make that promise: on any page carrying a
          layoutId the exit never finished and every later link landed
          blank. `initial={false}` keeps the server pass emitting the
          animate values, so the page is painted before hydration. */}
      <motion.div
        key={transitionKey}
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.32, ease: EASE }}
      >
        {children}
      </motion.div>
    </main>
  );

  /* The chat rail — the kit gives it its own header, level with the top bar,
     so it sits beside the centre column and not under it; under the
     Topbar layout's full-width bar it starts below the bar instead. On the
     right by default, on the left when the flag says so. */
  const chat = (
    <AnimatePresence initial={false}>
      {(chatVisible || guestChat) && (
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: chatWidth, opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={{ duration: 0.34, ease: EASE }}
          className={cn(
            "sticky hidden shrink-0 overflow-hidden xl:block",
            topbar ? "top-20 h-[calc(100dvh-80px)]" : "top-0 h-dvh",
          )}
        >
          <div className="h-full" style={{ width: chatWidth }}>
            {chatVisible ? (
              <ChatPanel side={chatLeft ? "left" : "right"} onClose={() => setChatOpen(false)} />
            ) : (
              <GuestChatPanel side={chatLeft ? "left" : "right"} onClose={() => setChatOpen(false)} />
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const overlays = (
    <>
      <MobileTabBar onMenu={() => setNavOpen((n) => !n)} menuOpen={navOpen} />
      <SignInModal
        open={signIn.open}
        initialEmail={signIn.email}
        onClose={() => setSignIn((current) => ({ ...current, open: false }))}
      />
      <WalletModal open={wallet.open} initialTab={wallet.tab} preset={wallet.preset} onClose={closeWallet} />
      {/* `?modal=deposit` and friends (lib/wallet/deep-link.ts). Suspense,
          because reading the query string must not hold the page's static
          render back. */}
      <Suspense fallback={null}>
        <WalletDeepLink />
      </Suspense>
      <KycModal open={kycOpen} onClose={closeKyc} />
      <TelegramMiniApp />
    </>
  );

  /* Smartico owns the sidebar's level and mission widgets when it is
     configured. It sits under the query provider because identification follows
     the session, and inside this branch rather than above it because a
     gamification tracker has no business on the admin dashboard. */
  return (
    <SmarticoProvider config={smartico}>
      <ToastProvider>
      <MotionConfig reducedMotion="user">
        <ContentContext.Provider value={content}>
        <SiteNameContext.Provider value={siteName}>
          <I18nProvider>
          <PlayerProvider>
            <ShellContext.Provider value={shell}>
            <PlatformEventsProvider>
              {topbar ? (
                /* Topbar: the bar spans the page; the chat and the page sit
                   side by side under it. */
                <div className={cn("flex min-h-dvh flex-col", canvas, boxed)}>
                  {skipLink}
                  {mobileDrawer}
                  {topBar}
                  <div className="flex min-w-0 flex-1">
                    {barRail}
                    {chatLeft && chat}
                    {main}
                    {!chatLeft && chat}
                  </div>
                  {overlays}
                </div>
              ) : (
                <div className={cn("flex min-h-dvh", canvas, boxed)}>
                  {skipLink}
                  {rail}
                  {chatLeft && chat}
                  {mobileDrawer}
                  <div className="flex min-w-0 flex-1 flex-col">
                    {topBar}
                    {tileLayout && <TileNav casinoNav={casinoNav} />}
                    {main}
                  </div>
                  {!chatLeft && chat}
                  {overlays}
                </div>
              )}
            </PlatformEventsProvider>
            </ShellContext.Provider>
          </PlayerProvider>
          </I18nProvider>
        </SiteNameContext.Provider>
        </ContentContext.Provider>
      </MotionConfig>
      </ToastProvider>
    </SmarticoProvider>
  );
}
